// @flow

// Libs
import React from 'react'
import _includes from 'lodash/includes'
import _isEmpty from 'lodash/isEmpty'
import _filter from 'lodash/filter'
import moment from 'moment'

// Constants
import {
  OPERATION_STATE,
  PROP_FARM,
  PROP_AVAILABLE_SOURCE_SEASONS,
  PROP_SOURCE_SEASON,
  PROP_DESTINATION_SEASON,
  PROP_AVAILABLE_DESTINATION_SEASONS,
  FIELDS_TO_MIGRATE,
} from 'hfty-model-asset-migrate'
import { ORDER_TEST, SHIPPING, RESULTS_POSTED } from 'hfty-model-soil-jobs'

// Components
import Select from 'react-select'
import Checkbox from '@react-mdc/checkbox'
import Button from '@react-mdc/button'

// Messages
import { FormattedMessage } from 'react-intl'
import { default as assetMigrate_messages } from '../../messages'

// Style
import { styles as s } from './style'

export default class Layout extends React.Component {
  props: {
    farms: Array,
    jobs: Array,
    fields: Array,
    seasons: Object,
    operation: Object,
    actions: {
      onSubmit: Function,
      onChangeOperation: Function,
      onCancelOperations: Function,
    },
  }

  componentWillReceiveProps(nextProps: Object) {
    if (nextProps.operation) {
      const asset = nextProps.operation.model[OPERATION_STATE]
      const prevPropsOp = this.props.operation

      if (_isEmpty(asset[FIELDS_TO_MIGRATE])) {
        this.generateInitialFields(nextProps)
      }

      if (asset[PROP_FARM]) {
        if (asset[PROP_FARM] !== prevPropsOp.model[OPERATION_STATE][PROP_FARM]) {
          this.generateSelectedFarmSeasonsAndFields(nextProps)
        }
      }

      if (asset[PROP_SOURCE_SEASON]) {
        if (asset[PROP_SOURCE_SEASON] !== prevPropsOp.model[OPERATION_STATE][PROP_SOURCE_SEASON]) {
          this.updateAvailableDestinationSeasons(nextProps)
        }
      }
    }
  }

  render(): React.Element<any> {
    const { operation } = this.props

    if (operation) {
      return (
        <div style={s.mainWrapper}>
          <div style={s.card}>
            <div style={s.cardHeader}>
              <FormattedMessage {...assetMigrate_messages['AssetMigrate.headerText']} />
            </div>

            <div style={s.farmAndSeasonSelectors}>
              <div style={s.seasonSelector}>
                <label htmlFor={PROP_FARM}>
                  <FormattedMessage {...assetMigrate_messages['AssetMigrate.farmToTransfer']} />
                </label>
                <Select
                  style={s.select}
                  clearable={false}
                  name={PROP_FARM}
                  value={this.getModelPropValue(PROP_FARM)}
                  options={this.props.farms}
                  onChange={(e: Object): Function => this.onFarmChange(e)}
                />
              </div>
              <div style={s.seasonSelector}>
                <label htmlFor={PROP_SOURCE_SEASON}>
                  <FormattedMessage {...assetMigrate_messages['AssetMigrate.seasonFrom']} />
                </label>
                <Select
                  style={s.select}
                  clearable={false}
                  name={PROP_SOURCE_SEASON}
                  disabled={_isEmpty(this.getModelPropValue(PROP_FARM))}
                  value={this.getModelPropValue(PROP_SOURCE_SEASON)}
                  options={this.getModelPropValue(PROP_AVAILABLE_SOURCE_SEASONS)}
                  onChange={(e: Object): Function => this.onSourceSeasonChange(e)}
                />
              </div>
              <div style={s.seasonSelector}>
                <label htmlFor={PROP_DESTINATION_SEASON}>
                  <FormattedMessage {...assetMigrate_messages['AssetMigrate.seasonTo']} />
                </label>
                <Select
                  style={s.select}
                  clearable={false}
                  name={PROP_DESTINATION_SEASON}
                  disabled={_isEmpty(this.getModelPropValue(PROP_SOURCE_SEASON))}
                  value={this.getModelPropValue(PROP_DESTINATION_SEASON)}
                  options={this.getModelPropValue(PROP_AVAILABLE_DESTINATION_SEASONS)}
                  onChange={(e: Object): Function => this.onDestinationSeasonChange(e)}
                />
              </div>
            </div>

            {this.renderFieldsTable()}

            {!_isEmpty(operation.errors) && this.renderErrors()}

            <div style={s.cardFooter}>
              <Button
                raised
                primary
                style={s.cardFooterButton}
                disabled={!this.checkFormSelectionStatus()}
                onClick={this.props.actions.onSubmit}
              >
                {!operation.model.isLoading ? 'SAVE' : 'Loading...'}
              </Button>
              <Button raised primary style={s.cardFooterButton} onClick={this.props.actions.onCancelOperations}>
                CANCEL
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return null
  }

  renderFieldRow = (field: Object, key: number): React.Element<any> => {
    if (this.props.operation) {
      return (
        <tr key={key}>
          <td style={s.tableSegment}>
            <Checkbox.Default
              inputId={field.id}
              name={field.label}
              checked={field.isSelected}
              disabled={_isEmpty(this.getModelPropValue(PROP_FARM))}
              onChange={(e: Object): Function => this.onFieldUpdate(e, 'isSelected')}
            />
          </td>
          <td style={s.tableSegment}>{field.id}</td>
          <td style={s.tableSegment}>{field.label}</td>
          <td style={s.tableSegment}>{field.category}</td>
          <td style={s.tableSegment}>
            <Checkbox.Default
              inputId={field.id}
              name={field.label}
              checked={field.shape}
              disabled={_isEmpty(this.getModelPropValue(PROP_FARM))}
              onChange={(e: Object): Function => this.onFieldUpdate(e, 'shape')}
            />
          </td>
          <td style={s.tableSegment}>
            <Checkbox.Default
              inputId={field.id}
              name={field.label}
              checked={field.grid}
              disabled={_isEmpty(this.getModelPropValue(PROP_FARM))}
              onChange={(e: Object): Function => this.onFieldUpdate(e, 'grid')}
            />
          </td>
        </tr>
      )
    }
  }

  renderErrors = (): React.Element<any> => {
    const { errors } = this.props.operation

    const _renderError = (error: Object, key: number): React.Element<any> => {
      if (error && error.id && error.error) {
        return (
          <li style={s.errorsItem} key={key}>
            <span style={s.errorItemKey}>{error.id}</span>
            <span style={s.errorItemBody}>{error.error}</span>
          </li>
        )
      }
    }

    switch (typeof errors) {
      case 'object':
        return <ul style={s.errors}>{errors.map(_renderError)}</ul>

      case 'string':
        return (
          <div style={s.errors}>
            <span style={s.singleErrorBody}>{errors}</span>
          </div>
        )
    }
  }

  renderFieldsTable = (): React.Element<any> => {
    const { operation } = this.props
    const hasValidFields = typeof operation.model[OPERATION_STATE][FIELDS_TO_MIGRATE] !== 'string'

    if (hasValidFields) {
      return (
        <div>
          <div style={s.fieldsSelectorsTable}>
            <div style={s.tableHeader}>
              <span style={s.tableHeaderFirst}>
                <FormattedMessage {...assetMigrate_messages['AssetMigrate.selectFields']} />
              </span>
              <span style={s.tableHeaderSecond}>
                <FormattedMessage {...assetMigrate_messages['AssetMigrate.selectBoxes']} />
              </span>
            </div>

            <table>
              <thead>
                <tr>
                  <th style={s.tableDefSegment} />
                  <th style={s.tableIdSegment}>ID</th>
                  <th style={s.tableLabelSegment}>Label</th>
                  <th style={s.tableDefSegment}>Type</th>
                  <th style={s.tableDefSegment}>Field Boundaries</th>
                  <th style={s.tablePointsSegment}>GPS Points & Grid Layout</th>
                </tr>
              </thead>

              <tbody>{operation.model[OPERATION_STATE][FIELDS_TO_MIGRATE].map(this.renderFieldRow)}</tbody>
            </table>
          </div>

          <div style={s.fieldsSelectorsTableFooter}>
            <Button
              disabled={_isEmpty(this.getModelPropValue(PROP_FARM))}
              style={s.fieldsSelectorsTableButton}
              onClick={this.onSelectAll}
            >
              SELECT ALL
            </Button>
          </div>
        </div>
      )
    } else {
      return (
        <div style={s.errors}>
          <span style={s.singleErrorBody}>{operation.model[OPERATION_STATE][FIELDS_TO_MIGRATE]}</span>
        </div>
      )
    }
  }

  onSelectAll = () => {
    const { actions, operation } = this.props

    actions.onChangeOperation({
      ...operation,
      model: {
        [OPERATION_STATE]: {
          ...operation.model[OPERATION_STATE],
          [FIELDS_TO_MIGRATE]: operation.model[OPERATION_STATE][FIELDS_TO_MIGRATE].map((field: Object): Object => {
            return { ...field, isSelected: true, shape: true, grid: true }
          }),
        },
      },
    })
  }

  getModelPropValue = (type: string): any => {
    if (this.props.operation) {
      return this.props.operation.model[OPERATION_STATE][type]
    }

    switch (type) {
      case PROP_FARM:
      case PROP_SOURCE_SEASON:
      case PROP_DESTINATION_SEASON:
        return {}

      case PROP_AVAILABLE_SOURCE_SEASONS:
      case PROP_AVAILABLE_DESTINATION_SEASONS:
        return []
    }
  }

  onFarmChange = (e: Object) => {
    const { operation, actions } = this.props

    actions.onChangeOperation({
      ...operation,
      errors: [],
      model: {
        [OPERATION_STATE]: {
          ...operation.model[OPERATION_STATE],
          [PROP_FARM]: { ...e },
        },
      },
    })
  }

  onSourceSeasonChange = (e: Object) => {
    const { operation, actions, fields } = this.props
    const selectedFarmId = operation.model[OPERATION_STATE][PROP_FARM].id
    const farmFields = fields.filter((field: Object): Object => field.parent === selectedFarmId)

    actions.onChangeOperation({
      ...operation,
      model: {
        [OPERATION_STATE]: {
          ...operation.model[OPERATION_STATE],
          [PROP_SOURCE_SEASON]: { ...e },
          [FIELDS_TO_MIGRATE]: this.getFieldsModels(farmFields, e.id),
        },
      },
    })
  }

  onDestinationSeasonChange = (e: Object) => {
    const { actions, operation } = this.props

    actions.onChangeOperation({
      ...operation,
      model: {
        [OPERATION_STATE]: {
          ...operation.model[OPERATION_STATE],
          [PROP_DESTINATION_SEASON]: { ...e },
        },
      },
    })
  }

  onFieldUpdate = (e: Object, prop: string) => {
    const fieldId = +e.target.id
    const { actions, operation } = this.props

    switch (prop) {
      case 'isSelected':
        actions.onChangeOperation({
          ...operation,
          model: {
            [OPERATION_STATE]: {
              ...operation.model[OPERATION_STATE],
              [FIELDS_TO_MIGRATE]: operation.model[OPERATION_STATE][FIELDS_TO_MIGRATE].map((field: Object): Object => {
                if (field.isSelected) {
                  return field.id === fieldId ? { ...field, isSelected: false, shape: false, grid: false } : field
                } else {
                  return field.id === fieldId ? { ...field, isSelected: true } : field
                }
              }),
            },
          },
        })
        break

      case 'shape':
      case 'grid':
        actions.onChangeOperation({
          ...operation,
          model: {
            [OPERATION_STATE]: {
              ...operation.model[OPERATION_STATE],
              [FIELDS_TO_MIGRATE]: operation.model[OPERATION_STATE][FIELDS_TO_MIGRATE].map((field: Object): Object => {
                if (!field.isSelected) {
                  return field.id === fieldId ? { ...field, isSelected: true, [prop]: true } : field
                } else {
                  return field.id === fieldId ? { ...field, [prop]: !field[prop] } : field
                }
              }),
            },
          },
        })
    }
  }

  checkFormSelectionStatus = (): boolean => {
    const { operation } = this.props

    const isValid =
      operation.model[OPERATION_STATE][PROP_FARM] &&
      operation.model[OPERATION_STATE][PROP_SOURCE_SEASON] &&
      operation.model[OPERATION_STATE][PROP_DESTINATION_SEASON] &&
      typeof operation.model[OPERATION_STATE][FIELDS_TO_MIGRATE] !== 'string' &&
      operation.model[OPERATION_STATE][FIELDS_TO_MIGRATE].some((field: Object): boolean => field.isSelected === true)

    return !!isValid
  }

  generateInitialFields = (props: Object) => {
    props.actions.onChangeOperation({
      ...props.operation,
      model: {
        [OPERATION_STATE]: {
          ...props.operation.model[OPERATION_STATE],
          [FIELDS_TO_MIGRATE]: this.getFieldsModels(props.fields),
        },
      },
    })
  }

  generateSelectedFarmSeasonsAndFields = (props: Object) => {
    const selectedFarmId = props.operation.model[OPERATION_STATE][PROP_FARM].id
    let fields = props.fields.filter((field: Object): Object => field.parent === selectedFarmId)

    props.actions.onChangeOperation({
      ...props.operation,
      model: {
        [OPERATION_STATE]: {
          ...props.operation.model[OPERATION_STATE],
          [PROP_AVAILABLE_SOURCE_SEASONS]: this.getSelectedFarmAvailableSeasons(props),
          [FIELDS_TO_MIGRATE]: this.getFieldsModels(fields),
        },
      },
    })
  }

  updateAvailableDestinationSeasons = (props: Object) => {
    props.actions.onChangeOperation({
      ...props.operation,
      model: {
        [OPERATION_STATE]: {
          ...props.operation.model[OPERATION_STATE],
          [PROP_AVAILABLE_DESTINATION_SEASONS]: this.getDestinationSeasons(props),
        },
      },
    })
  }

  getSelectedFarmAvailableSeasons = (props: Object): Array<number> => {
    const { operation, fields, seasons } = props
    const selectedFarmId = operation.model[OPERATION_STATE][PROP_FARM].id
    const correspondingFields = fields.filter((field: Object): Object => field.parent === selectedFarmId)
    const dateLength = seasons ? seasons[Object.keys(seasons)[0]].startDate.toString().length : 10

    /*
     * Get unique seasons ids
     * */
    const farmSeasonsIds = correspondingFields
      .map((field: Object): Object => field.season)
      .reduce((seasons: Array, season: number): Array => {
        if (seasons.indexOf(season) === -1) {
          seasons.push(season)
        }
        return seasons
      }, [])

    const correspondingSeasons = _filter(seasons, (season: Object): Object => {
      if (farmSeasonsIds.includes(season.id)) {
        return season
      }
    })

    const currentYearStartDate = +moment()
      .valueOf(new Date(new Date().getFullYear(), 0, 1))
      .toString()
      .slice(0, dateLength)

    return correspondingSeasons.filter((season: Object): Object => season.startDate < currentYearStartDate)
  }

  withJobsFilter = (fields: Array): Array => {
    const applicableStatuses = [ORDER_TEST, SHIPPING, RESULTS_POSTED]
    const { jobs } = this.props
    const allJobsFieldsIds = jobs.map((j: Object): number => j.field)
    const validJobsFieldsIds = jobs
      .filter((job: Object): Object => _includes(applicableStatuses, job.status))
      .map((job: Object): number => job.field)

    /*
     * Firstly check if field doesn't have associated job
     * if it has - job should be with valid status: "Order Test" or "Results Posted"
     * */
    return fields.filter((field: Object): boolean => {
      return !allJobsFieldsIds.includes(field.id) || validJobsFieldsIds.includes(field.id)
    })
  }

  getFieldsModels = (fields: Array, season: number): Array<Object> => {
    const filteredFields = !season
      ? this.withJobsFilter(fields)
      : this.withJobsFilter(fields.filter((field: Object): Object => field.season === season))

    if (!_isEmpty(filteredFields)) {
      return filteredFields.map((field: Object): Object => ({
        ...field,
        isSelected: false,
        shape: false,
        grid: false,
      }))
    }

    return 'No field to migrate'
  }

  getDestinationSeasons = (props: Object): Array<Object> => {
    return _filter(this.props.seasons, (season: Object): Object => {
      return moment(props.operation.model[OPERATION_STATE][PROP_SOURCE_SEASON].endDate).isBefore(
        moment(season.startDate)
      )
    })
  }
}
